import React from 'react';
import { motion } from 'framer-motion';
import { ShieldCheck, Handshake, Sprout, Compass } from 'lucide-react';

/**
 * Institutional pillars — substitutes the old "Numbers" section.
 * We removed fabricated metrics until we have real platform data.
 * The visual rhythm (4 cards in a glass-card container) is preserved.
 */
const pillars = [
  {
    icon: ShieldCheck,
    title: 'Cadastro verificado',
    desc: 'Identificamos quem cria conta para que cada Spark aconteça entre pessoas reais.',
    color: 'blue',
  },
  {
    icon: Handshake,
    title: 'Conexão direta',
    desc: 'Sem intermediários: investidor fala com empreendedor, e empreendedor fala com investidor.',
    color: 'gold',
  },
  {
    icon: Sprout,
    title: 'Pequenos negócios',
    desc: 'Nosso foco é o Brasil real — cafeterias, oficinas, lojas, projetos que mudam bairros.',
    color: 'blue',
  },
  {
    icon: Compass,
    title: 'Transparência total',
    desc: 'Quando tivermos números públicos, mostraremos. Por enquanto, só promessas verdadeiras.',
    color: 'gold',
  },
];

const NumbersSection = () => {
  return (
    <section className="relative py-24 lg:py-28" data-testid="numbers-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="relative overflow-hidden rounded-3xl glass-card p-8 sm:p-12 lg:p-16"
        >
          {/* Decorative background */}
          <div className="absolute inset-0 grid-pattern opacity-40 pointer-events-none" />
          <div className="absolute -top-32 -left-32 w-96 h-96 bg-[#4facfe]/15 rounded-full blur-3xl" />
          <div className="absolute -bottom-32 -right-32 w-96 h-96 bg-[#ffa726]/15 rounded-full blur-3xl" />

          <div className="relative z-10">
            <div className="text-center max-w-2xl mx-auto">
              <span className="eyebrow">Nossos princípios</span>
              <h2 className="mt-5 heading-display text-3xl sm:text-4xl lg:text-5xl text-white">
                Pequenos negócios, <span className="gradient-text">grandes histórias</span>.
              </h2>
              <p className="mt-4 text-sm sm:text-base text-slate-400 leading-relaxed">
                A GetSpark está nascendo — e queremos crescer com você. Estes são os compromissos
                que carregamos desde o primeiro dia.
              </p>
            </div>

            <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-7">
              {pillars.map((p, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: i * 0.1 }}
                  className="text-center group"
                  data-testid={`pillar-${i + 1}`}
                >
                  <div
                    className={`mx-auto w-14 h-14 rounded-2xl flex items-center justify-center mb-4 transition-all duration-500 ${
                      p.color === 'gold'
                        ? 'bg-gradient-to-br from-[#ffa726]/25 to-[#ff7043]/10 text-[#ffd54f] group-hover:shadow-[0_0_40px_rgba(255,167,38,0.5)]'
                        : 'bg-gradient-to-br from-[#4facfe]/25 to-[#1e90ff]/10 text-[#4facfe] group-hover:shadow-[0_0_40px_rgba(79,172,254,0.5)]'
                    }`}
                  >
                    <p.icon size={22} />
                  </div>
                  <div className="heading-display font-bold text-lg sm:text-xl text-white">{p.title}</div>
                  <div className="mt-2 text-xs sm:text-sm text-slate-400 leading-relaxed max-w-[230px] mx-auto">
                    {p.desc}
                  </div>
                </motion.div>
              ))}
            </div>

            <div className="mt-12 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-[11px] text-slate-500">
              <div className="flex items-center gap-2">
                <div className="w-1.5 h-1.5 rounded-full bg-[#4facfe]" /> Plataforma em construção
              </div>
              <div className="flex items-center gap-2">
                <div className="w-1.5 h-1.5 rounded-full bg-[#ffa726]" /> Primeiras Sparks acontecendo agora
              </div>
              <div className="flex items-center gap-2">
                <div className="w-1.5 h-1.5 rounded-full bg-green-400" /> Cadastro 100% gratuito
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default NumbersSection;
